import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { SmartToy as SmartToyIcon, CheckCircle as ApproveIcon, Edit as OverrideIcon } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useTranslation } from '../../i18n';
import {
    aiGradingReviewService,
    GradingReviewItem,
} from '../../services/aiGradingReviewService';

/**
 * Issue #197 (EU-KI-Act Art. 14 — Human-Oversight): Trainer-Review-Liste aller
 * KI-vorbewerteten Freitext-Antworten mit Score-Filter (Grenzfall-Pruefung),
 * Freigabe (approve) und Score/Feedback-Override.
 */
const STATUS_LABELS: Record<string, { label: string; color: 'warning' | 'success' | 'info' }> = {
    ai_pending_review: { label: 'Wartet auf Prüfung', color: 'warning' },
    teacher_reviewed: { label: 'Freigegeben', color: 'success' },
    teacher_overridden: { label: 'Übersteuert', color: 'info' },
};

const AiGradingReview: React.FC = () => {
    const { t } = useTranslation();
    const notify = useNotify();

    const [items, setItems] = useState<GradingReviewItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [status, setStatus] = useState('ai_pending_review');
    const [minScore, setMinScore] = useState('');
    const [maxScore, setMaxScore] = useState('');

    const [overrideItem, setOverrideItem] = useState<GradingReviewItem | null>(null);
    const [overrideScore, setOverrideScore] = useState('');
    const [overrideFeedback, setOverrideFeedback] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await aiGradingReviewService.list({
                status,
                minScore: minScore !== '' ? Number(minScore) : undefined,
                maxScore: maxScore !== '' ? Number(maxScore) : undefined,
            });
            setItems(result.items);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    }, [status, minScore, maxScore]);

    useEffect(() => {
        load();
    }, [load]);

    const handleApprove = async (item: GradingReviewItem) => {
        try {
            const result = await aiGradingReviewService.approve(item.id);
            notify(
                result.lessonCompleted
                    ? t('aiReview.approvedCompleted', 'Freigegeben — Lektion wurde abgeschlossen.')
                    : t('aiReview.approved', 'Bewertung freigegeben.'),
                { type: 'success' }
            );
            load();
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Fehler', { type: 'error' });
        }
    };

    const openOverride = (item: GradingReviewItem) => {
        setOverrideItem(item);
        setOverrideScore(item.score !== null ? String(item.score) : '');
        setOverrideFeedback(item.feedback || '');
    };

    const handleOverride = async () => {
        if (!overrideItem || overrideScore === '') return;
        try {
            await aiGradingReviewService.override(overrideItem.id, {
                score: Number(overrideScore),
                feedback: overrideFeedback,
            });
            notify(t('aiReview.overridden', 'Bewertung übersteuert.'), { type: 'success' });
            setOverrideItem(null);
            load();
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Fehler', { type: 'error' });
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SmartToyIcon color="primary" />
                <Typography variant="h5">
                    {t('aiReview.title', 'KI-Bewertungen prüfen')}
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('aiReview.subtitle', 'KI-vorbewertete Freitext-Antworten deiner Lernenden. Bei Kursen mit Pflicht-Review zählt die Bewertung erst nach deiner Freigabe zum Abschluss.')}
            </Typography>

            <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    label={t('aiReview.status', 'Status')}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="ai_pending_review">{STATUS_LABELS.ai_pending_review.label}</MenuItem>
                    <MenuItem value="teacher_reviewed">{STATUS_LABELS.teacher_reviewed.label}</MenuItem>
                    <MenuItem value="teacher_overridden">{STATUS_LABELS.teacher_overridden.label}</MenuItem>
                    <MenuItem value="all">{t('aiReview.all', 'Alle')}</MenuItem>
                </TextField>
                <TextField
                    size="small"
                    type="number"
                    label={t('aiReview.minScore', 'Score ab')}
                    value={minScore}
                    onChange={(e) => setMinScore(e.target.value)}
                    sx={{ width: 120 }}
                />
                <TextField
                    size="small"
                    type="number"
                    label={t('aiReview.maxScore', 'Score bis')}
                    value={maxScore}
                    onChange={(e) => setMaxScore(e.target.value)}
                    sx={{ width: 120 }}
                />
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : items.length === 0 ? (
                <Alert severity="info" data-testid="empty-state">
                    {t('aiReview.empty', 'Keine KI-Bewertungen für die aktuelle Filterung.')}
                </Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small" data-testid="review-table">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('aiReview.course', 'Kurs / Lektion')}</TableCell>
                                <TableCell>{t('aiReview.answer', 'Antwort')}</TableCell>
                                <TableCell align="right">{t('aiReview.score', 'KI-Score')}</TableCell>
                                <TableCell>{t('aiReview.statusCol', 'Status')}</TableCell>
                                <TableCell>{t('aiReview.date', 'Eingereicht')}</TableCell>
                                <TableCell align="right">{t('aiReview.actions', 'Aktionen')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{item.courseTitle}</Typography>
                                        <Typography variant="caption" color="text.secondary">{item.lessonTitle}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 320 }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                                            {item.answer}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" color={item.passed ? 'success.main' : 'warning.main'} fontWeight={600}>
                                            {item.score}{item.minScore != null ? ` / min. ${item.minScore}` : ''}
                                        </Typography>
                                        {item.originalAiScore !== null && (
                                            <Typography variant="caption" color="text.secondary">
                                                {t('aiReview.originalAi', 'KI ursprünglich')}: {item.originalAiScore}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={STATUS_LABELS[item.gradingStatus]?.label ?? item.gradingStatus}
                                            color={STATUS_LABELS[item.gradingStatus]?.color ?? 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {item.attemptedAt ? new Date(item.attemptedAt).toLocaleString('de-DE') : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {item.gradingStatus === 'ai_pending_review' && (
                                            <Button
                                                size="small"
                                                startIcon={<ApproveIcon />}
                                                onClick={() => handleApprove(item)}
                                                data-testid={`approve-${item.id}`}
                                            >
                                                {t('aiReview.approve', 'Freigeben')}
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            startIcon={<OverrideIcon />}
                                            onClick={() => openOverride(item)}
                                            data-testid={`override-${item.id}`}
                                        >
                                            {t('aiReview.override', 'Übersteuern')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={!!overrideItem} onClose={() => setOverrideItem(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('aiReview.overrideTitle', 'Bewertung übersteuern')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {overrideItem && (
                        <Alert severity="info" icon={<SmartToyIcon fontSize="small" />}>
                            {t('aiReview.overrideHint', 'KI-Vorschlag')}: {overrideItem.originalAiScore ?? overrideItem.score}
                            {overrideItem.minScore != null ? ` (min. ${overrideItem.minScore})` : ''}
                        </Alert>
                    )}
                    <TextField
                        label={t('aiReview.newScore', 'Neuer Score')}
                        type="number"
                        value={overrideScore}
                        onChange={(e) => setOverrideScore(e.target.value)}
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        label={t('aiReview.newFeedback', 'Feedback an die lernende Person')}
                        value={overrideFeedback}
                        onChange={(e) => setOverrideFeedback(e.target.value)}
                        multiline
                        rows={4}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOverrideItem(null)}>{t('aiReview.cancel', 'Abbrechen')}</Button>
                    <Button variant="contained" onClick={handleOverride} disabled={overrideScore === ''} data-testid="override-save">
                        {t('aiReview.save', 'Speichern')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AiGradingReview;
